import React, { useMemo, useState } from "react";
import { Alert, Button, Collapse, Modal, Space, Table, Tag, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { Booking } from "../../types/booking";
import type {
  CommercialInvoiceAnalysisResult,
  CommercialInvoiceDeepCheckComparison,
} from "./types";
import { getCommercialInvoiceValidityBreakdown } from "./commercialInvoiceValidity";
import { CommercialInvoiceValidityDisplay } from "./CommercialInvoiceValidityDisplay";
import {
  getCommercialInvoiceAnalysisIssues,
  getOverallCiVerdict,
} from "./commercialInvoiceAnalysisIssues";
import { formatCiDisplayValue } from "./formatCiDisplayValue";

const { Text } = Typography;

const COMPARISON_STATUS: Record<
  CommercialInvoiceDeepCheckComparison["status"],
  { label: string; color: string }
> = {
  match: { label: "Match", color: "success" },
  mismatch: { label: "Mismatch", color: "error" },
  missing_booking: { label: "Not on booking", color: "default" },
  missing_invoice: { label: "Not on invoice", color: "warning" },
};

export interface CommercialInvoiceAnalysisDetailModalProps {
  open: boolean;
  onClose: () => void;
  result: CommercialInvoiceAnalysisResult;
  booking?: Booking | null;
  loading?: boolean;
  onReanalyse: () => void;
  onOpenDeepCheck?: () => void;
}

export const CommercialInvoiceAnalysisDetailModal: React.FC<
  CommercialInvoiceAnalysisDetailModalProps
> = ({
  open,
  onClose,
  result,
  booking,
  loading = false,
  onReanalyse,
  onOpenDeepCheck,
}) => {
  const [showAllIssues, setShowAllIssues] = useState(false);

  const validity = useMemo(
    () => getCommercialInvoiceValidityBreakdown(result, booking),
    [result, booking]
  );

  const issues = useMemo(
    () => getCommercialInvoiceAnalysisIssues(result, booking, validity),
    [result, booking, validity]
  );

  const verdict = useMemo(
    () => getOverallCiVerdict(issues, validity),
    [issues, validity]
  );

  const visibleIssues = showAllIssues ? issues : issues.slice(0, 6);
  const hasDeepCheck = Boolean(result.deepCheck?.available);
  const aiComparisons = result.deepCheck?.bookingComparisons ?? [];
  const aiIssues = aiComparisons.filter((row) => row.status !== "match");

  const comparisonColumns: ColumnsType<CommercialInvoiceDeepCheckComparison> = [
    {
      title: "Result",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status: CommercialInvoiceDeepCheckComparison["status"]) => {
        const meta = COMPARISON_STATUS[status];
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    { title: "Field", dataIndex: "field", key: "field", width: 140, ellipsis: true },
    {
      title: "Booking",
      dataIndex: "bookingValue",
      key: "bookingValue",
      ellipsis: true,
      render: (v: unknown) => formatCiDisplayValue(v) ?? "—",
    },
    {
      title: "Invoice",
      dataIndex: "invoiceValue",
      key: "invoiceValue",
      ellipsis: true,
      render: (v: unknown) => formatCiDisplayValue(v) ?? "—",
    },
  ];

  const missingAutoFields = result.requiredFields.filter((f) => !f.present);

  const collapseItems = [
    ...(hasDeepCheck && aiComparisons.length > 0
      ? [
          {
            key: "ai-compare",
            label: `Booking vs invoice (AI) — ${aiIssues.length} issue${aiIssues.length === 1 ? "" : "s"}`,
            children: (
              <Table
                size="small"
                pagination={false}
                rowKey={(row, i) => `${row.field}-${i}`}
                dataSource={aiComparisons}
                columns={comparisonColumns}
                scroll={{ x: true }}
              />
            ),
          },
        ]
      : []),
    ...(!hasDeepCheck && missingAutoFields.length > 0
      ? [
          {
            key: "auto-fields",
            label: `Missing on invoice (automatic scan) — ${missingAutoFields.length}`,
            children: (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {missingAutoFields.map((field) => (
                  <li key={field.id} style={{ fontSize: 13, marginBottom: 4 }}>
                    {field.label}
                  </li>
                ))}
              </ul>
            ),
          },
        ]
      : []),
    {
      key: "technical",
      label: "File & scan details",
      children: (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Text style={{ fontSize: 12 }}>
            <Text type="secondary">File: </Text>
            {result.fileName}
          </Text>
          <Text style={{ fontSize: 12 }}>
            <Text type="secondary">Scan method: </Text>
            {result.extractionMethod === "excel_cells"
              ? "Excel"
              : result.extractionMethod === "pdf_text"
                ? "PDF text"
                : "Limited"}
          </Text>
          {result.signals.commercial.length > 0 || result.signals.proforma.length > 0 ? (
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                Document wording
              </Text>
              <Space wrap size={[4, 4]}>
                {result.signals.commercial.map((s) => (
                  <Tag key={`c-${s}`} color="green">
                    {s}
                  </Tag>
                ))}
                {result.signals.proforma.map((s) => (
                  <Tag key={`p-${s}`} color="orange">
                    {s}
                  </Tag>
                ))}
              </Space>
            </div>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title="Commercial invoice check"
      open={open}
      onCancel={onClose}
      width="min(640px, calc(100vw - 32px))"
      styles={{ body: { maxHeight: "75vh", overflowY: "auto" } }}
      footer={[
        hasDeepCheck && onOpenDeepCheck ? (
          <Button key="ai" onClick={onOpenDeepCheck}>
            Full AI report
          </Button>
        ) : null,
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="reanalyse"
          type="primary"
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={onReanalyse}
        >
          Re-analyse
        </Button>,
      ]}
    >
      <Alert
        type={verdict.tone}
        showIcon
        message={verdict.label}
        style={{ marginBottom: 12 }}
      />

      <CommercialInvoiceValidityDisplay breakdown={validity} compact showAiNote />

      {issues.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
            What to fix
          </Text>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {visibleIssues.map((issue) => (
              <li
                key={issue.id}
                style={{
                  fontSize: 13,
                  lineHeight: 1.45,
                  marginBottom: 8,
                  color: issue.severity === "error" ? "#cf1322" : undefined,
                }}
              >
                <Text strong={issue.severity === "error"}>{issue.title}</Text>
                {issue.detail ? (
                  <Text
                    type="secondary"
                    style={{ display: "block", fontSize: 11, marginTop: 2 }}
                  >
                    {issue.detail}
                  </Text>
                ) : null}
              </li>
            ))}
          </ul>
          {issues.length > 6 ? (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto" }}
              onClick={() => setShowAllIssues((v) => !v)}
            >
              {showAllIssues ? "Show fewer" : `Show all ${issues.length} items`}
            </Button>
          ) : null}
        </div>
      ) : (
        <Alert
          type="success"
          showIcon
          message="No issues flagged"
          style={{ marginTop: 16 }}
        />
      )}

      <Collapse
        ghost
        size="small"
        style={{ marginTop: 16 }}
        items={collapseItems}
      />
    </Modal>
  );
};
